import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Loader2, Send } from 'lucide-react';
import type { BrainCollectedState } from '../../services/brainService';

export type BrainLine = { role: 'user' | 'assistant'; content: string };

type Props = {
  title?: string;
  subtitle?: string;
  lines: BrainLine[];
  busy: boolean;
  onSend: (text: string) => Promise<void>;
  collectedState?: BrainCollectedState;
};

export default function BrainChatPanel({ title, subtitle, lines, busy, onSend, collectedState }: Props) {
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines, busy]);

  const submit = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    await onSend(text);
  };

  return (
    <div className="flex h-full min-h-0 flex-col rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">{title || 'Brain chat'}</h2>
        <p className="text-xs text-slate-500">{subtitle || 'Unified OMS/EPC/AMS AI Brain'}</p>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {lines.map((line, i) => (
          <div
            key={`${i}-${line.role}`}
            className={`max-w-[95%] rounded-lg px-3 py-2 text-sm ${
              line.role === 'user'
                ? 'ml-auto bg-slate-900 text-white'
                : 'mr-auto border border-slate-100 bg-slate-50 text-slate-800 [&_a]:text-amber-700 [&_code]:rounded [&_code]:bg-slate-200/80 [&_code]:px-1 [&_p]:my-1 [&_ul]:my-1 [&_ul]:list-inside [&_ul]:list-disc'
            }`}
          >
            {line.role === 'assistant' ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{line.content}</ReactMarkdown> : line.content}
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Thinking…
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {collectedState ? (
        <div className="border-t border-slate-100 bg-slate-50/70 p-3">
          <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Live state</div>
          <pre className="mt-1 max-h-36 overflow-auto rounded bg-slate-900 p-2 text-[11px] text-slate-100">
            {JSON.stringify(collectedState, null, 2)}
          </pre>
        </div>
      ) : null}

      <div className="border-t border-slate-100 p-3">
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void submit();
              }
            }}
            rows={2}
            placeholder="Ask AI Brain..."
            className="min-h-[44px] flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-400/50"
            disabled={busy}
          />
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || !input.trim()}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-amber-500 text-white shadow hover:bg-amber-600 disabled:opacity-40"
            aria-label="Send"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
